import * as fastboot from 'android-fastboot';
import { Adb } from '@yume-chan/adb';
import { WebAdbService } from './webadb.service';

export type RootLogLevel = 'info' | 'success' | 'error' | 'warning';
type LogCallback = (message: string, level?: RootLogLevel) => void;

export class RootService {
  private static instance: RootService;
  private logCallback?: LogCallback;

  private constructor() {}

  public static getInstance(): RootService {
    if (!RootService.instance) {
      RootService.instance = new RootService();
    }
    return RootService.instance;
  }

  public setLogCallback(callback: LogCallback) {
    this.logCallback = callback;
  }

  private log(message: string, level: RootLogLevel = 'info') {
    if (this.logCallback) {
      this.logCallback(message, level);
    } else {
      console.log(`[${level.toUpperCase()}] ${message}`);
    }
  }

  // 步骤 1: 推送 ksud 文件到手机
  async pushKsudFile(adb: Adb, fileData: Uint8Array, fileName: string): Promise<void> {
    this.log('正在推送 ksud 文件到手机...', 'info');

    const remotePath = `/data/local/tmp/${fileName}`;

    // 使用 WebADB 的 sync API 写入文件
    const sync = await adb.sync();
    try {
      // 创建 ReadableStream 从 Uint8Array
      const stream = new ReadableStream({
        start(controller) {
          controller.enqueue(fileData);
          controller.close();
        }
      });

      await sync.write({
        filename: remotePath,
        file: stream as any,
        mtime: Math.floor(Date.now() / 1000),
        permission: 0o777,
      });

      this.log('文件推送完成', 'success');
    } finally {
      sync.dispose();
    }
  }

  // 步骤 2: 设置文件权限
  async setFilePermissions(adb: Adb, filePath: string): Promise<void> {
    this.log(`正在设置文件权限: ${filePath}`, 'info');
    const service = WebAdbService.getInstance();
    await service.executeShell(adb, `chmod 777 ${filePath}`);
    this.log('权限设置完成', 'success');
  }

  // 步骤 3: 重启到 fastboot 模式
  async rebootToFastboot(adb: Adb): Promise<void> {
    this.log('正在重启到 Fastboot 模式...', 'info');
    const service = WebAdbService.getInstance();
    await service.executeShell(adb, 'reboot bootloader');
    this.log('设备正在重启，请等待进入 Fastboot 模式...', 'warning');
  }

  // 步骤 4: 执行 Fastboot 命令
  async executeFastbootCommand(onProgress?: (message: string) => void): Promise<boolean> {
    this.log('正在连接 Fastboot 设备...', 'info');

    let device: any = null;

    try {
      device = new fastboot.FastbootDevice();
      await device.connect();

      this.log('Fastboot 设备连接成功', 'success');

      // 执行第一个命令: 设置 SELinux 宽容模式
      onProgress?.('正在执行: oem set-gpu-preemption 0 androidboot.selinux=permissive');
      this.log('正在设置 SELinux 宽容模式...', 'info');
      await device.runCommand('oem set-gpu-preemption 0 androidboot.selinux=permissive');
      this.log('✓ SELinux 宽容模式设置完成', 'success');

      // 执行第二个命令: 继续启动系统
      onProgress?.('正在执行: continue (设备将重启)');
      this.log('正在继续启动系统...', 'info');

      try {
        await device.runCommand('continue');
        this.log('✓ continue 命令已发送，设备正在重启...', 'success');
      } catch (continueError: any) {
        // continue 命令执行后设备会重启，连接断开是正常的
        if (continueError.message?.includes('Socket') || continueError.message?.includes('stream') || continueError.message?.includes('transfer')) {
          this.log('✓ continue 命令已发送，设备正在重启...', 'success');
        } else {
          throw continueError;
        }
      }

      return true;
    } catch (error: any) {
      this.log(`✗ Fastboot 操作失败: ${error.message}`, 'error');
      return false;
    } finally {
      // 尝试断开连接（如果连接还存在）
      try {
        if (device) {
          device.disconnect();
        }
      } catch {
        // 忽略断开连接时的错误
      }
    }
  }

  // 步骤 5: 等待设备重新连接 ADB
  async waitForAdbReconnect(
    onWaitCallback?: (seconds: number) => void,
    onReconnectRequest?: (() => Promise<Adb | null>) | null,
    maxWaitSeconds = 60
  ): Promise<Adb | null> {
    this.log('设备正在重启，请准备重新连接...', 'warning');
    this.log('设备重启后需要重新授权 USB 连接', 'info');

    return new Promise((resolve) => {
      let elapsed = 0;
      let isReconnecting = false;
      let lastAttemptTime = 0;
      const reconnectInterval = 5; // 每5秒尝试一次重连

      const interval = setInterval(async () => {
        elapsed += 2;
        onWaitCallback?.(elapsed);

        // 等待至少 10 秒后开始尝试重连（给设备足够的重启时间）
        // 每隔 reconnectInterval 秒尝试一次，避免过于频繁
        if (elapsed >= 10 && !isReconnecting && onReconnectRequest) {
          const timeSinceLastAttempt = elapsed - lastAttemptTime;

          if (timeSinceLastAttempt >= reconnectInterval) {
            isReconnecting = true;
            lastAttemptTime = elapsed;

            const attemptNumber = Math.floor((elapsed - 10) / reconnectInterval) + 1;
            this.log(`正在尝试重新连接设备 (第 ${attemptNumber} 次)...`, 'info');

            try {
              const newAdb = await onReconnectRequest();
              if (newAdb) {
                clearInterval(interval);
                this.log('设备已重新连接', 'success');
                resolve(newAdb);
                return;
              } else {
                this.log('设备尚未就绪，继续等待...', 'warning');
              }
            } catch (error: any) {
              this.log(`重连失败: ${error.message}，将继续尝试...`, 'warning');
            } finally {
              isReconnecting = false;
            }
          }
        }

        // 超时处理
        if (elapsed >= maxWaitSeconds) {
          clearInterval(interval);
          this.log('等待设备连接超时，请手动重新连接', 'error');
          resolve(null);
        }
      }, 2000);
    });
  }

  // 步骤 6: 执行 root 权限获取
  async executeRootExploit(adb: Adb): Promise<boolean> {
    this.log('正在执行 root 权限获取...', 'info');

    const service = WebAdbService.getInstance();

    try {
      // 执行 mqsas 命令获取权限
      const cmd = 'service call miui.mqsas.IMQSNative 21 i32 1 s16 "/data/local/tmp/ksud-aarch64-linux-android" i32 1 s16 \'late-load\' s16 \'/sdcard/ksulog.txt\' i32 60';
      this.log('执行权限获取命令...', 'info');
      const result = await service.executeShell(adb, cmd, 60000);
      this.log(`权限获取命令执行结果: ${result}`, 'info');

      return true;
    } catch (error: any) {
      this.log(`权限获取失败: ${error.message}`, 'error');
      return false;
    }
  }

  // 步骤 7: 写入并执行 root_final.sh 脚本
  async executeRootFinalScript(adb: Adb): Promise<boolean> {
    this.log('正在写入 root_final.sh 脚本...', 'info');

    const service = WebAdbService.getInstance();

    const scriptContent = `#!/system/bin/sh
RESETPROP=/data/adb/ksu/bin/resetprop
$RESETPROP ro.debuggable 1
$RESETPROP ro.secure 0
$RESETPROP service.adb.root 1
$RESETPROP persist.sys.usb.config adb
stop adbd
sleep 2
start adbd
echo DONE >> /sdcard/root_final_log.txt
`;

    // 写入脚本文件
    const lines = scriptContent.split('\n');
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (i === 0) {
        await service.executeShell(adb, `echo '${line}' > /data/local/tmp/root_final.sh`);
      } else if (line) {
        await service.executeShell(adb, `echo '${line}' >> /data/local/tmp/root_final.sh`);
      }
    }

    // 设置脚本权限
    await service.executeShell(adb, 'chmod 755 /data/local/tmp/root_final.sh');

    this.log('脚本写入完成，正在执行...', 'info');

    try {
      // 执行脚本
      const cmd = 'service call miui.mqsas.IMQSNative 21 i32 1 s16 "/system/bin/sh" i32 1 s16 "/data/local/tmp/root_final.sh" s16 "/sdcard/root_final_log.txt" i32 15';
      const result = await service.executeShell(adb, cmd, 30000);
      this.log(`脚本执行结果: ${result}`, 'info');

      // 等待 adbd 重启
      this.log('等待 adbd 重启...', 'info');
      await new Promise(resolve => setTimeout(resolve, 5000));

      return true;
    } catch (error: any) {
      this.log(`脚本执行失败: ${error.message}`, 'error');
      return false;
    }
  }

  // 验证 root 状态
  async verifyRootStatus(adb: Adb): Promise<{ isRooted: boolean; details: string }> {
    const service = WebAdbService.getInstance();

    try {
      const idResult = await service.executeShell(adb, 'id');
      const adbRootProp = await service.executeShell(adb, 'getprop service.adb.root');

      const isRooted = idResult.includes('uid=0') || adbRootProp.trim() === '1';

      return {
        isRooted,
        details: `id: ${idResult}\nservice.adb.root: ${adbRootProp}`
      };
    } catch (error: any) {
      return {
        isRooted: false,
        details: `验证失败: ${error.message}`
      };
    }
  }
}

export const rootService = RootService.getInstance();